import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';

export type ViewRole = 'developer' | 'office' | 'technician' | 'installer' | 'salesperson';

export interface ViewPerson {
  id:    number;
  name:  string;
  role:  string;
  color: string | null;
}

interface RoleContextValue {
  activeRole:    ViewRole;
  activePerson:  ViewPerson | null;
  realRole:      string;
  isOverride:    boolean;
  setActiveRole: (role: ViewRole) => void;
  setActivePerson: (person: ViewPerson | null) => void;
  resetRole:     () => void;
}

const RoleContext = createContext<RoleContextValue>({
  activeRole:      'developer',
  activePerson:    null,
  realRole:        'developer',
  isOverride:      false,
  setActiveRole:   () => {},
  setActivePerson: () => {},
  resetRole:       () => {},
});

const SESSION_ROLE   = 'gws_view_role';
const SESSION_PERSON = 'gws_view_person';

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const realRole = (user?.role ?? 'office') as ViewRole;

  const [activeRole, setActiveRoleState] = useState<ViewRole>(() => {
    const saved = sessionStorage.getItem(SESSION_ROLE) as ViewRole | null;
    return saved ?? realRole;
  });

  const [activePerson, setActivePersonState] = useState<ViewPerson | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_PERSON) ?? 'null'); }
    catch { return null; }
  });

  useEffect(() => {
    const savedRole   = sessionStorage.getItem(SESSION_ROLE) as ViewRole | null;
    const savedPerson = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_PERSON) ?? 'null'); } catch { return null; } })();
    setActiveRoleState(savedRole ?? realRole);
    setActivePersonState(savedPerson);
  }, [user?.username]); // eslint-disable-line

  const setActiveRole = useCallback((role: ViewRole) => {
    setActiveRoleState(role);
    setActivePersonState(null);
    sessionStorage.setItem(SESSION_ROLE, role);
    sessionStorage.removeItem(SESSION_PERSON);
  }, []);

  const setActivePerson = useCallback((person: ViewPerson | null) => {
    setActivePersonState(person);
    if (person) sessionStorage.setItem(SESSION_PERSON, JSON.stringify(person));
    else sessionStorage.removeItem(SESSION_PERSON);
  }, []);

  const resetRole = useCallback(() => {
    setActiveRoleState(realRole);
    setActivePersonState(null);
    sessionStorage.removeItem(SESSION_ROLE);
    sessionStorage.removeItem(SESSION_PERSON);
  }, [realRole]);

  const isOverride = activeRole !== realRole || activePerson !== null;

  return (
    <RoleContext.Provider value={{
      activeRole,
      activePerson,
      realRole,
      isOverride,
      setActiveRole,
      setActivePerson,
      resetRole,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
